// A stable color palette
export const sectionPalette = [
  "#e6194b",
  "#3cb44b",
  "#ffe119",
  "#4363d8",
  "#f58231",
  "#911eb4",
  "#46f0f0",
  "#f032e6",
  "#bcf60c",
  "#fabebe",
  "#008080",
  "#e6beff",
  "#9a6324",
  "#fffac8",
  "#800000",
  "#aaffc3",
  "#808000",
  "#ffd8b1",
  "#000075",
  "#808080",
];

/**
 * Stable, deterministic color by id (string or guid).
 * Uses a simple string hash to pick a color from palette.
 */

export function colorForId(id) {
  const s = String(id ?? "");
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 70%)`; // nice pastel colors
}

// --- In ../colorUtils (additions) ---

// Optional: keep your existing exports (sectionPalette, colorForId, etc.)

function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return h | 0;
}

function hueDistance(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Collision-resistant color picker.
 * - Base hue from hash(id)
 * - Probe around the circle by golden angle until min separation is met
 * - Deterministic if you iterate a stable, sorted set of IDs.
 */
export function colorForIdWithProbing(id, usedHues, opts = {}) {
  const minDist = opts.minHueDistance ?? 22; // degrees (tweak if you have many items)
  const sat = opts.saturation ?? 65; // %
  const light = opts.lightness ?? 70; // %
  const maxProbes = opts.maxProbes ?? 720; // safety
  const goldenAngle = 137.508;

  let hue = ((Math.abs(hashStr(String(id))) % 360) + 360) % 360;

  for (let i = 0; i < maxProbes; i++) {
    const ok = usedHues.every((h) => hueDistance(h, hue) >= minDist);
    if (ok) {
      return { color: `hsl(${hue.toFixed(2)}, ${sat}%, ${light}%)`, hue };
    }
    hue = (hue + goldenAngle) % 360;
  }

  // If too dense, return last probe (you can also start a second "ring" by changing sat/light here)
  return { color: `hsl(${hue.toFixed(2)}, ${sat}%, ${light}%)`, hue };
}

/**
 * Builds a unique color map for a set of IDs with probing to avoid collisions.
 * Deterministic: sorts IDs so assignment is stable across renders.
 */
export function buildColorMapWithProbing(ids, opts = {}) {
  const unique = Array.from(new Set((ids || []).filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b)),
  );

  const used = [];
  const map = new Map();
  for (const id of unique) {
    const { color, hue } = colorForIdWithProbing(id, used, opts);
    used.push(hue);
    map.set(id, color);
  }
  return map;
}
// Tag color map
const tagColors = {
  A: "#e6194b", B: "#3cb44b", C: "#ffe119", D: "#4363d8", E: "#f58231",
  F: "#911eb4", G: "#46f0f0", H: "#f032e6", I: "#bcf60c", J: "#fabebe",
  K: "#008080", L: "#e6beff", M: "#9a6324", N: "#fffac8", O: "#800000",
  P: "#aaffc3", Q: "#808000", R: "#ffd8b1", S: "#000075", T: "#808080",
  U: "#ffffff", V: "#000000", W: "#ff69b4", X: "#7fffd4", Y: "#ffb6c1", Z: "#4682b4",
};

export function getColorByTag(tag) {
    return tagColors[tag?.toUpperCase()] || "#95a5a6";
}

export function hexToRGBA(hex, alpha = 1) {
    const bigint = parseInt(hex.replace("#", ""), 16);
    const r = ((bigint >> 16) & 255) / 255;
    const g = ((bigint >> 8) & 255) / 255;
    const b = (bigint & 255) / 255;
    return [r, g, b, alpha];
}

export function createMaterial(renderer, allocator, color, alpha = 1) {
    const matId = allocator.getMaterialIndex();

    renderer.setMaterial(matId, {
        diffuse: hexToRGBA(color, alpha)
    });

    return matId;
}


export function createHighlightMaterial(renderer, allocator) {
    const matId = allocator.getMaterialIndex();

    renderer.setMaterial(matId, {
        diffuse: [0, 0.7, 1, 1] // strong blue highlight
    });

    return matId;
}



